import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import React, { useEffect, useRef, useMemo } from "react";
import { useTraceContext } from "../trace";
import { cn } from "@/lib/utils";
// Intentionally distinct from brand colors so formula references stay easy to tell apart.
const REFERENCE_COLORS = [
    { text: '#5B97FF', bg: '#EFF5FF' },
    { text: '#FF616B', bg: '#FFEFF0' },
    { text: '#C277E6', bg: '#F9F0FF' },
    { text: '#5FB157', bg: '#F1F9F1' },
    { text: '#E67399', bg: '#FFF0F5' },
    { text: '#E69138', bg: '#FFF7EF' },
    { text: '#00ABC0', bg: '#F0FDFF' },
];
function buildPatternForDisplay(display) {
    const bangIdx = display.indexOf("!");
    let sheetPart = "";
    let refPart = display;
    if (bangIdx !== -1) {
        const rawSheet = display.slice(0, bangIdx);
        const escaped = rawSheet
            .replace(/[.*+?^${}()|[\]\\]/g, "\\$&")
            // In formula text an apostrophe inside a quoted sheet name is doubled.
            .replace(/'/g, "''");
        sheetPart = `'?${escaped}'?!`;
        refPart = display.slice(bangIdx + 1);
    }
    let processedRef;
    if (/^\d+:\d+$/.test(refPart)) {
        const [r1, r2] = refPart.split(":");
        processedRef = `\\$?${r1}:\\$?${r2}`;
    }
    else if (/^[A-Z]+:[A-Z]+$/.test(refPart)) {
        const [c1, c2] = refPart.split(":");
        const esc1 = c1.split("").map((c) => `\\$?${c}`).join("");
        const esc2 = c2.split("").map((c) => `\\$?${c}`).join("");
        processedRef = `${esc1}:${esc2}`;
    }
    else {
        processedRef = refPart.replace(/([A-Z]+)(\d+)/g, (_, col, row) => {
            const escapedCol = col
                .split("")
                .map((c) => `\\$?${c}`)
                .join("");
            return `${escapedCol}\\$?${row}`;
        });
    }
    // Boundary assertions so "A1" cannot match inside "A10", "Other!A1", or
    // "A1:B2" — mirrors the parser's own boundary rules.
    return `(?<![A-Za-z0-9_$:!])${sheetPart}${processedRef}(?![A-Za-z0-9_(:])`;
}
// Blank out string-literal contents (same length, so indices are preserved).
// The parser ignores refs inside strings; occurrence counting must match.
function maskStringLiterals(formula) {
    return formula.replace(/"[^"]*"/g, (m) => `"${" ".repeat(m.length - 2)}"`);
}
function computeColorAssignments(precedents, originSheet) {
    const assignments = new Array(precedents.length).fill(null);
    const counters = { root: 0 };
    for (let i = 0; i < precedents.length; i++) {
        const depth = precedents[i].depth || 1;
        let parentSheet = originSheet;
        let parentKey = "root";
        if (depth > 1) {
            for (let j = i - 1; j >= 0; j--) {
                if ((precedents[j].depth || 1) === depth - 1) {
                    parentSheet = precedents[j].sheet;
                    parentKey = String(j);
                    break;
                }
            }
        }
        if (!(parentKey in counters))
            counters[parentKey] = 0;
        if (precedents[i].sheet === parentSheet) {
            assignments[i] = counters[parentKey] % REFERENCE_COLORS.length;
            counters[parentKey]++;
        }
    }
    return assignments;
}
function getFormulaSiblings(precedents, selectedIndex, colorAssignments) {
    const targetDepth = selectedIndex > 0 ? (precedents[selectedIndex - 1].depth || 1) : 1;
    if (targetDepth === 1) {
        return precedents
            .map((p, i) => ({ ...p, flatIndex: i, colorIndex: colorAssignments[i] }))
            .filter((p) => (p.depth || 1) === 1);
    }
    let parentIdx = -1;
    for (let i = selectedIndex - 2; i >= 0; i--) {
        if ((precedents[i].depth || 1) === targetDepth - 1) {
            parentIdx = i;
            break;
        }
    }
    const siblings = [];
    for (let i = parentIdx + 1; i < precedents.length; i++) {
        const d = precedents[i].depth || 1;
        if (d < targetDepth)
            break;
        if (d === targetDepth) {
            siblings.push({
                ...precedents[i],
                flatIndex: i,
                colorIndex: colorAssignments[i],
            });
        }
    }
    return siblings;
}
function highlightFormulaWithColors(formula, siblings, selectedFlatIndex) {
    if (!formula || siblings.length === 0)
        return [_jsx("span", { children: formula }, "f")];
    const masked = maskStringLiterals(formula);
    const matches = [];
    for (const sib of siblings) {
        const pattern = buildPatternForDisplay(sib.display);
        const regex = new RegExp(pattern, "gi");
        let m;
        let occCount = 0;
        while ((m = regex.exec(masked)) !== null) {
            if (occCount === (sib.occurrence || 0)) {
                matches.push({
                    start: m.index,
                    end: m.index + m[0].length,
                    text: m[0],
                    colorIndex: sib.colorIndex,
                    isSelected: sib.flatIndex === selectedFlatIndex,
                });
                break;
            }
            occCount++;
        }
    }
    matches.sort((a, b) => a.start - b.start);
    const filtered = [];
    for (const match of matches) {
        if (filtered.length === 0 || match.start >= filtered[filtered.length - 1].end) {
            filtered.push(match);
        }
    }
    const spans = [];
    let lastEnd = 0;
    for (const match of filtered) {
        if (match.start > lastEnd) {
            spans.push(_jsx("span", { children: formula.slice(lastEnd, match.start) }, `t${lastEnd}`));
        }
        const color = match.colorIndex != null ? REFERENCE_COLORS[match.colorIndex] : null;
        const style = {};
        if (color) {
            style.color = color.text;
        }
        if (match.isSelected) {
            style.backgroundColor = color ? color.bg : "hsl(var(--accent))";
            style.borderRadius = "3px";
            style.padding = "1px 3px";
        }
        spans.push(_jsx("span", { style: style, children: match.text }, `m${match.start}`));
        lastEnd = match.end;
    }
    if (lastEnd < formula.length) {
        spans.push(_jsx("span", { children: formula.slice(lastEnd) }, `t${lastEnd}`));
    }
    return spans;
}
function formatValue(v) {
    if (v === null || v === undefined)
        return "\u2013";
    return v.displayValue != null ? v.displayValue : "\u2013";
}
export default function TraceOverlay() {
    const { traceOpen: visible, traceData, traceSelectedIndex: selectedIndex, traceMode: mode, handleTraceSelect: onSelect, handleCloseTrace: onCloseTrace, handleExpandNode, handleExpandDependentNode, handleCollapseNode: onCollapse, } = useTraceContext();
    const onExpand = mode === "dependents" ? handleExpandDependentNode : handleExpandNode;
    const rowsRef = useRef(null);
    useEffect(() => {
        if (!visible || !traceData)
            return;
        function handleKeyDown(e) {
            const maxIndex = traceData.precedents.length;
            if (e.key === "ArrowDown") {
                e.preventDefault();
                const next = Math.min(selectedIndex + 1, maxIndex);
                onSelect(next);
            }
            else if (e.key === "ArrowUp") {
                e.preventDefault();
                const prev = Math.max(selectedIndex - 1, 0);
                onSelect(prev);
            }
            else if (e.key === "ArrowRight") {
                if (selectedIndex > 0) {
                    const node = traceData.precedents[selectedIndex - 1];
                    if (node.hasFormula && !node.expanded) {
                        e.preventDefault();
                        onExpand(selectedIndex - 1);
                    }
                }
            }
            else if (e.key === "ArrowLeft") {
                if (selectedIndex > 0) {
                    const node = traceData.precedents[selectedIndex - 1];
                    if (node.expanded) {
                        e.preventDefault();
                        onCollapse(selectedIndex - 1);
                    }
                }
            }
            else if (e.key === "Escape") {
                e.preventDefault();
                void onCloseTrace();
            }
        }
        document.addEventListener("keydown", handleKeyDown);
        return () => document.removeEventListener("keydown", handleKeyDown);
    }, [visible, selectedIndex, traceData, onSelect, onCloseTrace, onExpand, onCollapse]);
    useEffect(() => {
        if (!visible || !rowsRef.current)
            return;
        const active = rowsRef.current.querySelector("[data-selected='true']");
        if (active)
            active.scrollIntoView({ block: "nearest" });
    }, [visible, selectedIndex]);
    const colorAssignments = useMemo(() => traceData
        ? computeColorAssignments(traceData.precedents, traceData.originSheet)
        : [], [traceData]);
    const formulaSiblings = useMemo(() => traceData
        ? getFormulaSiblings(traceData.precedents, selectedIndex, colorAssignments)
        : [], [traceData, selectedIndex, colorAssignments]);
    if (!visible || !traceData)
        return null;
    const { formula, originCell, originSheet, precedents } = traceData;
    const originValue = traceData.originValue;
    const selectedPrecedent = selectedIndex > 0 ? precedents[selectedIndex - 1] : null;
    let displayFormula = formula;
    if (selectedPrecedent && (selectedPrecedent.depth ?? 1) > 1) {
        const selIdx = selectedIndex - 1;
        let parentFormula = null;
        for (let i = selIdx - 1; i >= 0; i--) {
            if (precedents[i].depth === (selectedPrecedent.depth ?? 1) - 1) {
                parentFormula = precedents[i].formula ?? null;
                break;
            }
        }
        displayFormula = parentFormula ?? formula;
    }
    const selectedFlatIndex = selectedIndex > 0 ? selectedIndex - 1 : -1;
    return (_jsx("div", { className: "flex h-full flex-col bg-background", children: _jsxs("div", { className: "flex-1 overflow-auto", ref: rowsRef, children: [_jsx("div", { className: "border-b border-border px-3 py-2.5", children: _jsx("span", { className: "font-mono text-sm leading-relaxed text-foreground", children: mode === "dependents"
                            ? `${originSheet}!${originCell}`
                            : displayFormula
                                ? highlightFormulaWithColors(displayFormula, formulaSiblings, selectedFlatIndex)
                                : "\u2013" }) }), _jsxs("div", { className: "flex items-center justify-between border-b border-border bg-muted/30 px-3 py-1.5", children: [_jsx("span", { className: "text-xs font-medium text-muted-foreground", children: mode === "dependents" ? "Dependents" : "Precedents" }), _jsx("span", { className: "text-xs font-medium text-muted-foreground", children: "Value" })] }), _jsxs("div", { "data-selected": selectedIndex === 0, className: cn("flex cursor-pointer items-center justify-between px-3 py-2 text-sm transition-colors", selectedIndex === 0 ? "bg-accent" : "hover:bg-muted/50"), onClick: () => onSelect(0), children: [_jsxs("span", { className: "font-medium text-foreground", children: [originSheet, "!", originCell] }), _jsx("span", { className: "text-muted-foreground", children: originValue != null ? String(originValue) : "\u2013" })] }), precedents.length === 0 && (_jsx("div", { className: "px-3 py-2 text-sm italic text-muted-foreground", children: mode === "dependents" ? "No Dependent" : "No Precedent" })), precedents.map((prec, i) => {
                    const depth = prec.depth || 1;
                    const indent = 12 + Math.min(depth, 8) * 16;
                    const refColor = colorAssignments[i] != null
                        ? REFERENCE_COLORS[colorAssignments[i]]
                        : null;
                    const isSelected = selectedIndex === i + 1;
                    const hasFormula = prec.hasFormula;
                    const isCircular = prec.isCircular;
                    const treeKey = prec.treeKey;
                    let indicator = null;
                    if (isCircular) {
                        indicator = (_jsx("span", { className: "mr-1 text-xs text-destructive", title: "Circular reference", children: "\u21BB" }));
                    }
                    else if (hasFormula && prec.expanded) {
                        indicator = (_jsx("span", { className: "mr-1 cursor-pointer text-[10px] text-muted-foreground hover:text-foreground", onClick: (e) => { e.stopPropagation(); onCollapse(i); }, children: "\u25BC" }));
                    }
                    else if (hasFormula && !prec.expanded) {
                        indicator = (_jsx("span", { className: "mr-1 cursor-pointer text-[10px] text-muted-foreground hover:text-foreground", onClick: (e) => { e.stopPropagation(); onExpand(i); }, children: "\u25B6" }));
                    }
                    else {
                        indicator = _jsx("span", { className: "mr-1 inline-block w-[10px]" });
                    }
                    const rowStyle = { paddingLeft: indent };
                    if (isSelected) {
                        if (refColor) {
                            rowStyle.borderLeftColor = refColor.text;
                            rowStyle.borderLeftWidth = "3px";
                            rowStyle.borderLeftStyle = "solid";
                            rowStyle.background = refColor.bg;
                        }
                        else {
                            rowStyle.background = "hsl(var(--accent))";
                        }
                    }
                    return (_jsxs("div", { "data-selected": isSelected, className: cn("flex cursor-pointer items-center py-1.5 pr-3 text-sm transition-colors", isSelected ? "" : "hover:bg-muted/50"), style: rowStyle, onClick: () => onSelect(i + 1), children: [indicator, _jsx("span", { className: "flex-1 truncate", style: refColor ? { color: refColor.text } : undefined, children: prec.display }), _jsx("span", { className: "ml-2 shrink-0 text-muted-foreground", children: formatValue(prec.value) })] }, treeKey || `${prec.sheet}!${prec.ref}:${prec.occurrence}`));
                })] }) }));
}
